import React, { useState, useEffect, useRef } from "react";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../../_helpers";
import "./GlobalSearch.scss";
import { getSidebarData } from "../../../sidebar/SidebarData";

export default function GlobalSearch({
  permissions,
  generalSettings,
  taxonomy,
  navigate,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);

  const getAllSearchableItems = () => {
    const sidebarData = getSidebarData(permissions, generalSettings, taxonomy);
    const items = [];

    sidebarData.forEach((item) => {
      if (item.subItems.length === 0) {
        items.push({
          title: item.title,
          path: item.path,
          type: "parent",
        });
      }

      if (item.subItems && item.subItems.length > 0) {
        item.subItems.forEach((subItem) => {
          items.push({
            title: `${item.title} > ${subItem.name}`,
            path: subItem.path,
            type: "child",
            parent: item.title,
          });
        });
      }
    });

    return items;
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      return;
    }

    const allItems = getAllSearchableItems();
    const filtered = allItems.filter((item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setSuggestions(filtered.slice(0, 8));
    setShowSuggestions(filtered.length > 0);
    setSelectedIndex(-1);
  }, [searchQuery, permissions, generalSettings, taxonomy]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleNavigate(suggestions[selectedIndex].path);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleNavigate = (path) => {
    if (navigate) {
      navigate(path);
    } else {
      window.location.href = path;
    }
    setSearchQuery("");
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const highlightText = (text, query) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <strong key={index} className="text-primary">
          {part}
        </strong>
      ) : (
        part
      )
    );
  };

  const handleClear = () => {
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  return (
    <div
      className="global-search-wrapper d-md-flex d-none py-4 position-relative"
      ref={searchRef}>
      <span className="global-search-icon d-block">
        <SVG
          src={toAbsoluteUrl("media/svg/icons/NewDashboard/search-icon.svg")}
        />
      </span>
      <input
        className="global-search-input border-0"
        type="text"
        placeholder="Search anything..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => searchQuery && setShowSuggestions(true)}
      />
      {searchQuery && (
        <span
          className="global-search-clear-icon"
          role="button"
          onClick={handleClear}>
          <SVG src={toAbsoluteUrl("media/svg/icons/General/clear.svg")} />
        </span>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div className="search-suggestions thin-scrollbar">
          {suggestions.map((item, index) => (
            <div
              key={index}
              className={`search-suggestion-item ${
                index === selectedIndex ? "active" : ""
              }`}
              onClick={() => handleNavigate(item.path)}>
              <div className="suggestion-content">
                <div className="suggestion-text">
                  <div className="suggestion-title mb-0">
                    {highlightText(item.title, searchQuery)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
